import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowUpRight } from "lucide-react";

const drawerVariants = {
  hidden: { x: "100%", opacity: 0 },
  visible: {
    x: 0,
    opacity: 1,
    transition: { duration: 0.65, ease: [0.16, 1, 0.3, 1] },
  },
  exit: {
    x: "100%",
    opacity: 0,
    transition: { duration: 0.4, ease: [0.4, 0, 1, 1] },
  },
};

const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.3 } },
  exit: { opacity: 0, transition: { duration: 0.3 } },
};

const CaseStudyDrawer = ({ study, onClose }) => {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  useEffect(() => {
    const handleKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose]);

  if (!study) return null;

  const { accent, accentGlow } = study;
  const isLive = study.status === "Live";

  return (
    <AnimatePresence>
      {/* Backdrop */}
      <motion.div
        key="cs-backdrop"
        variants={backdropVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        style={{
          position: "fixed", inset: 0, zIndex: 90,
          background: "rgba(0,0,0,0.78)",
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
        }}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <motion.div
        key="cs-drawer"
        variants={drawerVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        role="dialog"
        aria-modal="true"
        aria-label={`Case study: ${study.title}`}
        style={{
          position: "fixed", right: 0, top: 0,
          zIndex: 100,
          height: "100dvh",
          width: "min(520px, 95vw)",
          display: "flex",
          flexDirection: "column",
          background: "#09090B",
          borderLeft: "1px solid rgba(255,255,255,0.07)",
          overflow: "hidden",
        }}
      >
        {/* Top accent line */}
        <div style={{
          height: "2px", flexShrink: 0,
          background: `linear-gradient(90deg, transparent 0%, ${accent} 40%, ${accent} 60%, transparent 100%)`,
        }} />

        {/* Top bar */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "14px 24px", flexShrink: 0,
          borderBottom: "1px solid rgba(255,255,255,0.05)",
        }}>
          <p style={{
            fontFamily: "var(--font-robert-medium)", fontSize: "8px",
            letterSpacing: "0.32em", color: "rgba(255,255,255,0.25)",
            textTransform: "uppercase",
          }}>
            Case Study
          </p>
          <button
            onClick={onClose}
            aria-label="Close case study"
            style={{
              display: "flex", alignItems: "center", justifyContent: "center",
              width: 30, height: 30, borderRadius: "6px",
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.08)",
              color: "rgba(255,255,255,0.35)", cursor: "pointer",
              transition: "background 0.2s, color 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(255,255,255,0.1)";
              e.currentTarget.style.color = "#ffffff";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(255,255,255,0.04)";
              e.currentTarget.style.color = "rgba(255,255,255,0.35)";
            }}
          >
            <X size={13} />
          </button>
        </div>

        {/* Scrollable content */}
        <div style={{ flex: 1, overflowY: "auto", scrollbarWidth: "none" }}>

          {/* Hero image */}
          {study.hoverImg && (
            <div style={{ position: "relative", flexShrink: 0 }}>
              <img
                src={study.hoverImg}
                alt={`${study.title} preview`}
                draggable={false}
                style={{
                  width: "100%",
                  height: "clamp(170px,26vh,260px)",
                  objectFit: "cover",
                  display: "block",
                }}
              />
              <div style={{
                position: "absolute", inset: 0,
                background: "linear-gradient(to top, #09090B 0%, rgba(9,9,11,0.55) 45%, rgba(9,9,11,0.05) 100%)",
              }} />
              <div style={{
                position: "absolute", top: "-20px", right: "-20px",
                width: "140px", height: "140px", borderRadius: "50%",
                background: `radial-gradient(circle, ${accent}30 0%, transparent 70%)`,
                filter: "blur(28px)", pointerEvents: "none",
              }} />
            </div>
          )}

          {/* Body */}
          <div style={{ padding: "0 clamp(16px,3.5vw,32px) clamp(24px,4vw,48px)" }}>

            {/* Context tag + title */}
            <div style={{ marginBottom: "16px" }}>
              <p style={{
                fontFamily: "var(--font-robert-medium)", fontSize: "8px",
                letterSpacing: "0.28em", textTransform: "uppercase",
                color: accent, marginBottom: "8px",
              }}>
                {study.context}
              </p>
              <h2 style={{
                fontFamily: "var(--font-robert-medium)", fontWeight: 700,
                fontSize: "clamp(1.9rem,4.5vw,3rem)",
                lineHeight: 1.02, color: "#F5F5F7",
                letterSpacing: "-0.025em",
              }}>
                {study.title}
              </h2>
            </div>

            {/* Status + role badges */}
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "16px", flexWrap: "wrap" }}>
              <span style={{
                display: "inline-flex", alignItems: "center", gap: "5px",
                padding: "4px 10px", borderRadius: "9999px",
                fontFamily: "var(--font-robert-medium)", fontSize: "8px",
                letterSpacing: "0.22em", textTransform: "uppercase",
                background: isLive ? "rgba(52,199,89,0.12)" : `${accent}14`,
                color: isLive ? "#34C759" : accent,
                border: isLive ? "1px solid rgba(52,199,89,0.25)" : `1px solid ${accent}32`,
              }}>
                {isLive && (
                  <span style={{
                    width: 5, height: 5, borderRadius: "50%",
                    background: "#34C759", flexShrink: 0,
                    animation: "pulse-live 1.6s ease-in-out infinite",
                  }} />
                )}
                {study.status}
              </span>
              <span style={{
                fontFamily: "var(--font-robert-medium)", fontSize: "8px",
                letterSpacing: "0.18em", textTransform: "uppercase",
                color: "rgba(255,255,255,0.28)",
              }}>
                {study.role}
              </span>
            </div>

            {/* View Project */}
            <a
              href={study.link}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "inline-flex", alignItems: "center", gap: "6px",
                padding: "9px 20px", borderRadius: "9999px",
                background: accent, color: "#09090B",
                fontFamily: "var(--font-robert-medium)",
                fontSize: "clamp(0.58rem,0.78vw,0.66rem)",
                fontWeight: 700, letterSpacing: "0.12em",
                textDecoration: "none", textTransform: "uppercase",
                boxShadow: `0 4px 18px ${accent}44`,
                marginBottom: "20px",
              }}
            >
              View Project <ArrowUpRight size={11} />
            </a>

            {/* Thin rule */}
            <div style={{
              height: "1px", marginBottom: "20px",
              background: `linear-gradient(90deg, ${accent}40, rgba(255,255,255,0.04) 60%, transparent)`,
            }} />

            {/* Description */}
            <p style={{
              fontFamily: "var(--font-robert-regular)",
              fontSize: "clamp(0.82rem,1.1vw,0.94rem)",
              color: "rgba(255,255,255,0.45)", lineHeight: 1.72,
              marginBottom: "28px",
            }}>
              {study.description}
            </p>

            {/* The Problem */}
            <div style={{ marginBottom: "28px" }}>
              <p style={{
                fontFamily: "var(--font-robert-medium)", fontSize: "8px",
                letterSpacing: "0.3em", textTransform: "uppercase",
                color: "rgba(255,255,255,0.22)", marginBottom: "12px",
              }}>
                The Problem
              </p>
              <p style={{
                fontFamily: "var(--font-robert-medium)",
                fontSize: "clamp(0.8rem,1.05vw,0.92rem)",
                color: "rgba(255,255,255,0.58)", lineHeight: 1.65,
              }}>
                {study.problem}
              </p>
            </div>

            {/* Approach */}
            <div style={{ marginBottom: "28px" }}>
              <p style={{
                fontFamily: "var(--font-robert-medium)", fontSize: "8px",
                letterSpacing: "0.3em", textTransform: "uppercase",
                color: "rgba(255,255,255,0.22)", marginBottom: "14px",
              }}>
                Approach
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: "11px" }}>
                {study.approach.map((item, i) => (
                  <div key={i} style={{ display: "flex", gap: "14px", alignItems: "flex-start" }}>
                    <span style={{
                      fontFamily: "var(--font-robert-medium)", fontSize: "9px",
                      color: accent, flexShrink: 0, marginTop: "4px",
                      letterSpacing: "0.06em", fontWeight: 700,
                    }}>
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <p style={{
                      fontFamily: "var(--font-robert-medium)",
                      fontSize: "clamp(0.76rem,1vw,0.86rem)",
                      color: "rgba(255,255,255,0.58)", lineHeight: 1.6,
                    }}>
                      {item}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Stack */}
            <div style={{ marginBottom: "32px" }}>
              <p style={{
                fontFamily: "var(--font-robert-medium)", fontSize: "8px",
                letterSpacing: "0.3em", textTransform: "uppercase",
                color: "rgba(255,255,255,0.22)", marginBottom: "12px",
              }}>
                Stack
              </p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                {study.stack.map((tech) => (
                  <span
                    key={tech}
                    style={{
                      fontFamily: "var(--font-robert-medium)",
                      fontSize: "clamp(0.6rem,0.8vw,0.7rem)",
                      fontWeight: 600, letterSpacing: "0.04em",
                      padding: "5px 13px", borderRadius: "9999px",
                      background: `${accent}14`,
                      border: `1px solid ${accent}32`,
                      color: accent,
                    }}
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>

            <div style={{ height: "40px" }} aria-hidden="true" />
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CaseStudyDrawer;
